/**
 * @file layer-service.ts
 * @description Provides core data operations for managing document layers (CRUD operations).
 */

import { 
    g, 
    layers, 
    LayerClass, 
    LayerAddAction, 
    LayerDeleteAction, 
    LayerMoveAction, 
    LayerPropertyAction 
} from '@hcie/core';
import { EventBus } from '@hcie/shared';

/**
 * @function getActiveLayer
 * @description Returns the currently active layer object.
 * @returns {LayerClass | null}
 */
export function getActiveLayer(): LayerClass | null {
    return layers[g.activeLayerIndex] || null;
}

/**
 * @function addLayer
 * @description Adds a new layer at the current index and pushes to history.
 * @param {string} [name] - Optional name for the new layer.
 * @returns {void}
 */
export function addLayer(name?: string): void {
    // [CRITICAL_LOGIC_START]
    const nextIndex: number = g.activeLayerIndex + 1;
    const layerName: string = name || `Layer ${layers.length}`;
    const newLayer = new LayerClass(layerName, g.image_width, g.image_height);

    if (name?.toLowerCase().includes('vector')) {
        newLayer.type = 'vector';
        newLayer.shapes = [];
    }

    layers.splice(nextIndex, 0, newLayer);
    g.activeLayerIndex = nextIndex;

    if (window.historyManager) {
        window.historyManager.push(new LayerAddAction(nextIndex, newLayer));
    }

    notifyLayerChange();
    // [CRITICAL_LOGIC_END]
}

/**
 * @function deleteLayer
 * @description Removes the active layer unless it is the last one.
 * @returns {void}
 */
export function deleteLayer(): void {
    if (layers.length <= 1) {
        console.warn("[LayerService] Cannot delete the last remaining layer.");
        return;
    }

    const indexToRemove: number = g.activeLayerIndex;
    const layerToDelete: LayerClass = layers[indexToRemove];

    layers.splice(indexToRemove, 1);

    if (window.historyManager) {
        window.historyManager.push(new LayerDeleteAction(indexToRemove, layerToDelete));
    }

    // Adjust active index after deletion
    if (g.activeLayerIndex >= layers.length) {
        g.activeLayerIndex = layers.length - 1;
    }

    notifyLayerChange();
}

/**
 * @function selectLayer
 * @description Sets the active layer index.
 * @param {number} index - The index to select.
 * @returns {void}
 */
export function selectLayer(index: number): void {
    if (index < 0 || index >= layers.length) {
        return;
    }
    g.activeLayerIndex = index;
    EventBus.emit('UPDATE_LAYER_PANEL');
}

/**
 * @function toggleLayerVisibility
 * @description Toggles the visible property of a layer.
 * @param {number} index - Index of the target layer.
 * @returns {void}
 */
export function toggleLayerVisibility(index: number): void {
    if (index < 0 || index >= layers.length) return;

    const targetLayer = layers[index];
    const previousVisibility = targetLayer.visible;
    targetLayer.visible = !previousVisibility;

    if (window.historyManager) {
        window.historyManager.push(new LayerPropertyAction(index, 'visible', previousVisibility, targetLayer.visible));
    }

    notifyLayerChange();
}

/**
 * @function moveLayer
 * @description reorders a layer in the stack.
 * @param {number} fromIndex - Original index.
 * @param {number} toIndex - Destination index.
 * @returns {void}
 */
export function moveLayer(fromIndex: number, toIndex: number): void {
    if (fromIndex < 0 || fromIndex >= layers.length) return;
    if (toIndex < 0 || toIndex >= layers.length) return;

    if (window.historyManager) {
        window.historyManager.push(new LayerMoveAction(fromIndex, toIndex));
    }

    const [movedLayer] = layers.splice(fromIndex, 1);
    layers.splice(toIndex, 0, movedLayer);

    // Update active index tracking
    if (g.activeLayerIndex === fromIndex) {
        g.activeLayerIndex = toIndex;
    } else if (fromIndex < g.activeLayerIndex && toIndex >= g.activeLayerIndex) {
        g.activeLayerIndex--;
    } else if (fromIndex > g.activeLayerIndex && toIndex <= g.activeLayerIndex) {
        g.activeLayerIndex++;
    }

    notifyLayerChange();
}

/**
 * @function renameLayer
 * @description Updates the name of a layer.
 * @param {number} index - Layer index.
 * @param {string} newName - New name to assign.
 * @returns {void}
 */
export function renameLayer(index: number, newName: string): void {
    if (index < 0 || index >= layers.length) return;

    const targetLayer = layers[index];
    const oldName = targetLayer.name;
    
    if (oldName === newName) return;
    
    targetLayer.name = newName;

    if (window.historyManager) {
        window.historyManager.push(new LayerPropertyAction(index, 'name', oldName, newName));
    }

    EventBus.emit('UPDATE_LAYER_PANEL');
}

/**
 * @function moveLayerUp
 * @description Moves the active layer one step up in the stack.
 * @returns {void}
 */
export function moveLayerUp(): void {
    if (g.activeLayerIndex < layers.length - 1) {
        moveLayer(g.activeLayerIndex, g.activeLayerIndex + 1);
    }
}

/**
 * @function moveLayerDown
 * @description Moves the active layer one step down in the stack.
 * @returns {void}
 */
export function moveLayerDown(): void {
    if (g.activeLayerIndex > 0) {
        moveLayer(g.activeLayerIndex, g.activeLayerIndex - 1);
    }
}

/**
 * @function notifyLayerChange
 * @description Internal helper to trigger global UI refreshes after layer structural changes.
 * @private
 */
function notifyLayerChange(): void {
    EventBus.emit('RENDER_LAYERS');
    EventBus.emit('UPDATE_LAYER_PANEL');
}
